import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getPayStubDetail,
  listPayStubEmployees,
  updatePayStubDetail,
} from "../api/hrPaystubs";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./HrSectionCompact.css";

const toInputValue = (value) =>
  value == null ? "" : String(value);

const normalizeText = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const parseOptionalNumber = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
};

const formatRole = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const buildFormState = (data) => ({
  legal_first_name: data?.legal_first_name || "",
  legal_last_name: data?.legal_last_name || "",
  date_of_birth: data?.date_of_birth || "",
  address_line1: data?.address_line1 || "",
  address_line2: data?.address_line2 || "",
  city: data?.city || "",
  province: data?.province || "",
  postal_code: data?.postal_code || "",
  country: data?.country || "Canada",
  worker_type: data?.worker_type || "employee",
  employment_start_date: data?.employment_start_date || "",
  pay_frequency: data?.pay_frequency || "",
  pay_type: data?.pay_type || "",
  hourly_rate: toInputValue(data?.hourly_rate),
  salary_amount: toInputValue(data?.salary_amount),
  overtime_multiplier: toInputValue(
    data?.overtime_multiplier == null ? "1.50" : data?.overtime_multiplier
  ),
  td1_federal_claim_amount: toInputValue(data?.td1_federal_claim_amount),
  td1_provincial_claim_amount: toInputValue(data?.td1_provincial_claim_amount),
  additional_tax_withholding: toInputValue(
    data?.additional_tax_withholding == null ? "0.00" : data?.additional_tax_withholding
  ),
  vacation_pay_percent: toInputValue(
    data?.vacation_pay_percent == null ? "4.00" : data?.vacation_pay_percent
  ),
  vacation_pay_method: data?.vacation_pay_method || "",
});

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Manage employee pay stub profile details used for payroll setup.",
    anchorId: "paystubs-overview",
    bullets: [
      "Select a staff member from the employee list.",
      "Update legal, tax, and pay setup information.",
      "Save changes to keep payroll records current.",
    ],
    tips: [
      "Always review legal name and SIN before payroll runs.",
      "Use inactive filter only when updating old records.",
    ],
  },
  {
    id: "employees",
    title: "Employees",
    description: "Find and select staff members to edit pay stub details.",
    anchorId: "paystubs-employees",
    bullets: [
      "Search by staff display name.",
      "Use Show inactive to include former staff.",
      "Status chips show SIN and direct deposit setup.",
    ],
    tips: ["Pick one employee, then complete all sections before saving."],
  },
  {
    id: "legal",
    title: "Legal identity",
    description: "Store legal identity information for payroll and tax reporting.",
    anchorId: "paystubs-legal",
    bullets: [
      "Legal first/last name should match official records.",
      "Date of birth is used for payroll profile matching.",
      "SIN can be updated securely from this section.",
    ],
    tips: ["Only update SIN when confirmed from trusted employee documents."],
  },
  {
    id: "address",
    title: "Home address",
    description: "Keep employee home address details current for records and slips.",
    anchorId: "paystubs-address",
    bullets: [
      "Update address line, city, province, and postal code.",
      "Country defaults to Canada unless updated.",
    ],
    tips: ["Update address before generating year-end tax slips."],
  },
  {
    id: "employment",
    title: "Employment and pay setup",
    description: "Set worker type, pay schedule, and pay calculation settings.",
    anchorId: "paystubs-employment",
    bullets: [
      "Choose worker type and start date.",
      "Set pay frequency and pay type.",
      "Fill hourly rate or salary and overtime multiplier.",
    ],
    tips: ["Salary amount is per pay period, not annual amount."],
  },
  {
    id: "tax",
    title: "Tax and vacation",
    description: "Enter TD1 claim values, additional tax, and vacation settings.",
    anchorId: "paystubs-tax",
    bullets: [
      "Federal and provincial claim amounts come from TD1 forms.",
      "Additional tax withholding is extra per pay run.",
      "Vacation pay method controls accrual or payout behavior.",
    ],
    tips: ["Recheck claim amounts when employee tax forms are updated."],
  },
  {
    id: "deposit",
    title: "Direct deposit",
    description: "Save banking setup securely for payroll deposits.",
    anchorId: "paystubs-deposit",
    bullets: [
      "Add institution, transit, and account numbers together.",
      "Bank info is encrypted after save.",
      "Direct deposit status shows whether details are on file.",
    ],
    tips: ["Double-check bank numbers before saving to avoid failed deposits."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Employee not showing",
    body: "Clear search and enable Show inactive if needed.",
  },
  {
    title: "Cannot save",
    body: "Check required values and verify SIN has 9 digits when entered.",
  },
  {
    title: "Direct deposit not saved",
    body: "Enter all three values: institution, transit, and account.",
  },
];

const TOOLTIPS = {
  search_staff: "Search by employee display name.",
  show_inactive: "Include inactive staff in the employee list.",
  legal_name: "Use legal names that match tax documents.",
  dob: "Date of birth used for payroll profile records.",
  sin: "SIN is masked and stored securely.",
  worker_type: "Choose employee or contractor for payroll handling.",
  pay_frequency: "How often this employee is paid.",
  pay_type: "Determines whether payroll uses hourly or salary fields.",
  overtime_multiplier: "Overtime rate multiplier for eligible hours.",
  federal_td1: "Federal TD1 claim amount from the employee form.",
  provincial_td1: "Provincial TD1 claim amount from the employee form.",
  vacation_method: "Choose to accrue vacation or pay each pay cycle.",
  direct_deposit: "Banking details are encrypted and hidden after save.",
};

export default function HRPayStubs() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const modalText = "text-sm md:text-base";
  const [employees, setEmployees] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [detailMeta, setDetailMeta] = useState({
    sin_on_file: false,
    sin_masked: "",
    direct_deposit_on_file: false,
  });
  const [formData, setFormData] = useState(buildFormState(null));
  const [showSinInput, setShowSinInput] = useState(false);
  const [sinValue, setSinValue] = useState("");
  const [showDirectDeposit, setShowDirectDeposit] = useState(false);
  const [bankFields, setBankFields] = useState({
    institution: "",
    transit: "",
    account: "",
  });
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) =>
      (emp.display_name || "").toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  useEffect(() => {
    if (!token) return;
    const loadEmployees = async () => {
      setLoadingList(true);
      setError("");
      setNotice("");
      setIsUnauthorized(false);
      try {
        const data = await listPayStubEmployees(includeInactive, token);
        setEmployees(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.response?.status === 403) {
          setIsUnauthorized(true);
        } else {
          setError(err?.response?.data?.detail || "Unable to load employees.");
        }
        setEmployees([]);
      } finally {
        setLoadingList(false);
      }
    };
    loadEmployees();
  }, [includeInactive, token]);

  useEffect(() => {
    if (!employees.length) {
      setSelectedStaffId(null);
      return;
    }
    const exists = employees.some((emp) => emp.staff_id === selectedStaffId);
    if (!exists) {
      setSelectedStaffId(employees[0]?.staff_id ?? null);
    }
  }, [employees, selectedStaffId]);

  useEffect(() => {
    if (!token || !selectedStaffId) return;
    const loadDetail = async () => {
      setLoadingDetail(true);
      setError("");
      setNotice("");
      setFieldErrors({});
      try {
        const data = await getPayStubDetail(selectedStaffId, token);
        setFormData(buildFormState(data));
        setDetailMeta({
          sin_on_file: Boolean(data?.sin_on_file),
          sin_masked: data?.sin_masked || "",
          direct_deposit_on_file: Boolean(data?.direct_deposit_on_file),
        });
        setShowSinInput(false);
        setSinValue("");
        setShowDirectDeposit(false);
        setBankFields({ institution: "", transit: "", account: "" });
      } catch (err) {
        if (err?.response?.status === 403) {
          setIsUnauthorized(true);
        } else {
          setError(err?.response?.data?.detail || "Unable to load employee details.");
        }
      } finally {
        setLoadingDetail(false);
      }
    };
    loadDetail();
  }, [selectedStaffId, token]);

  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateBankField = (field, value) => {
    setBankFields((prev) => ({ ...prev, [field]: value }));
  };

  const validateBeforeSave = () => {
    const nextErrors = {};
    const trimmedSin = sinValue.replace(/\\s+/g, "");
    if (showSinInput && trimmedSin) {
      const digitsOnly = trimmedSin.replace(/[^0-9]/g, "");
      if (digitsOnly.length !== 9) {
        nextErrors.sin = "SIN must be 9 digits.";
      }
    }
    if (showDirectDeposit) {
      const inst = bankFields.institution.trim();
      const transit = bankFields.transit.trim();
      const account = bankFields.account.trim();
      const anyProvided = inst || transit || account;
      if (anyProvided && (!inst || !transit || !account)) {
        nextErrors.direct_deposit = "Enter institution, transit, and account numbers.";
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!token || !selectedStaffId) return;
    if (!validateBeforeSave()) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        legal_first_name: normalizeText(formData.legal_first_name),
        legal_last_name: normalizeText(formData.legal_last_name),
        date_of_birth: formData.date_of_birth || null,
        address_line1: normalizeText(formData.address_line1),
        address_line2: normalizeText(formData.address_line2),
        city: normalizeText(formData.city),
        province: normalizeText(formData.province),
        postal_code: normalizeText(formData.postal_code),
        country: normalizeText(formData.country),
        worker_type: normalizeText(formData.worker_type),
        employment_start_date: formData.employment_start_date || null,
        pay_frequency: normalizeText(formData.pay_frequency),
        pay_type: normalizeText(formData.pay_type),
        hourly_rate: parseOptionalNumber(formData.hourly_rate),
        salary_amount: parseOptionalNumber(formData.salary_amount),
        overtime_multiplier: parseOptionalNumber(formData.overtime_multiplier),
        td1_federal_claim_amount: parseOptionalNumber(formData.td1_federal_claim_amount),
        td1_provincial_claim_amount: parseOptionalNumber(formData.td1_provincial_claim_amount),
        additional_tax_withholding: parseOptionalNumber(formData.additional_tax_withholding),
        vacation_pay_percent: parseOptionalNumber(formData.vacation_pay_percent),
        vacation_pay_method: normalizeText(formData.vacation_pay_method),
      };

      const trimmedSin = sinValue.replace(/\\s+/g, "");
      if (showSinInput && trimmedSin) {
        payload.sin = trimmedSin;
      }

      if (showDirectDeposit) {
        const inst = bankFields.institution.trim();
        const transit = bankFields.transit.trim();
        const account = bankFields.account.trim();
        if (inst && transit && account) {
          payload.direct_deposit = {
            institution: inst,
            transit,
            account,
          };
        }
      }

      const saved = await updatePayStubDetail(selectedStaffId, payload, token);
      setFormData(buildFormState(saved));
      setDetailMeta({
        sin_on_file: Boolean(saved?.sin_on_file),
        sin_masked: saved?.sin_masked || "",
        direct_deposit_on_file: Boolean(saved?.direct_deposit_on_file),
      });
      setShowSinInput(false);
      setSinValue("");
      setShowDirectDeposit(false);
      setBankFields({ institution: "", transit: "", account: "" });
      setNotice("Pay stub details saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save pay stub details.");
    } finally {
      setSaving(false);
    }
  };

  const payType = formData.pay_type || "";
  const isContractor = formData.worker_type === "contractor";

  const getCardClassName = useCallback(
    (id) =>
      `rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4 ${getSectionClassName(
        id
      )}`,
    [getSectionClassName]
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/hr/training");
  };

  return (
    <div className="hrSectionShell">
      <div className={`hrSectionWide hr-section-compact ${modalText}`}>
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Pay Stubs / Pay Slips
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            <div id="paystubs-overview">
              <ReportHelpCard
                title="Help Guide: Pay Stubs"
                subtitle="Store employee pay stub details, tax inputs, and direct deposit setup."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}
            {isUnauthorized && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                Access denied. Please contact an owner or manager for HR access.
              </div>
            )}
            {!isUnauthorized && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.8fr_1.2fr]">
                <div id="paystubs-employees" className={getCardClassName("paystubs-employees")}>
                  <div>
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Employees
                    </h2>
                    <p className="text-sm text-slate-500">
                      Select a staff member to manage pay stub details.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-600">Search staff</label>
                      <InfoTooltip text={TOOLTIPS.search_staff} label="Search staff" />
                    </div>
                    <input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search staff..."
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      Show inactive
                      <InfoTooltip text={TOOLTIPS.show_inactive} label="Show inactive" />
                    </label>
                  </div>
                  <div className="space-y-2 max-h-[520px] overflow-auto pr-1">
                    {loadingList && (
                      <div className="text-xs text-slate-500">Loading employees...</div>
                    )}
                    {!loadingList && employees.length === 0 && (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                        No employees found.
                      </div>
                    )}
                    {!loadingList &&
                      filteredEmployees.map((emp) => {
                        const isActive = emp.staff_id === selectedStaffId;
                        return (
                          <button
                            key={emp.staff_id}
                            type="button"
                            onClick={() => setSelectedStaffId(emp.staff_id)}
                            className={`w-full rounded-xl border px-3 py-2 text-left text-xs transition ${
                              isActive
                                ? "border-slate-300 bg-slate-50 text-slate-700"
                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-semibold text-sm truncate">
                                  {emp.display_name}
                                </div>
                                <div className="text-[11px] opacity-80">
                                  {formatRole(emp.role) || "Staff"}
                                </div>
                              </div>
                              {!emp.is_active && (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span
                                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                  emp.sin_on_file
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-slate-50 text-slate-500"
                                }`}
                              >
                                SIN
                              </span>
                              <span
                                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                  emp.direct_deposit_on_file
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-slate-50 text-slate-500"
                                }`}
                              >
                                Direct Deposit
                              </span>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>

                <div className="space-y-6">
                  {!selectedStaffId && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 text-sm text-slate-600">
                      Select a staff member to view pay stub details.
                    </div>
                  )}

                  {selectedStaffId && (
                    <>
                  <div id="paystubs-legal" className={getSectionClassName("paystubs-legal")}>
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Legal Identity
                      </h2>
                      <p className="text-sm text-slate-500">
                        Use the employee legal name for tax reporting and pay stubs.
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Legal First Name</label>
                          <InfoTooltip text={TOOLTIPS.legal_name} label="Legal name" />
                        </div>
                        <input
                          value={formData.legal_first_name}
                          onChange={(e) => updateFormField("legal_first_name", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Legal Last Name</label>
                          <InfoTooltip text={TOOLTIPS.legal_name} label="Legal name" />
                        </div>
                        <input
                          value={formData.legal_last_name}
                          onChange={(e) => updateFormField("legal_last_name", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Date of Birth</label>
                          <InfoTooltip text={TOOLTIPS.dob} label="Date of birth" />
                        </div>
                        <input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => updateFormField("date_of_birth", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">SIN</label>
                          <InfoTooltip text={TOOLTIPS.sin} label="SIN" />
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                          <span className="text-slate-600">
                            {detailMeta.sin_on_file
                              ? detailMeta.sin_masked || "On file"
                              : "No SIN on file"}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowSinInput((prev) => !prev)}
                            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            {showSinInput ? "Cancel" : "Update SIN"}
                          </button>
                        </div>
                      </div>
                    </div>
                    {showSinInput && (
                      <div>
                        <label className="text-sm text-slate-600">Enter new SIN</label>
                        <input
                          type="password"
                          autoComplete="off"
                          inputMode="numeric"
                          value={sinValue}
                          onChange={(e) => setSinValue(e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        {fieldErrors.sin && (
                          <div className="mt-1 text-xs text-rose-600">{fieldErrors.sin}</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div id="paystubs-address" className={getCardClassName("paystubs-address")}>
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">Home Address</h2>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="text-sm text-slate-600">Address Line 1</label>
                        <input
                          value={formData.address_line1}
                          onChange={(e) => updateFormField("address_line1", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-sm text-slate-600">Address Line 2</label>
                        <input
                          value={formData.address_line2}
                          onChange={(e) => updateFormField("address_line2", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">City</label>
                        <input
                          value={formData.city}
                          onChange={(e) => updateFormField("city", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Province</label>
                        <input
                          value={formData.province}
                          onChange={(e) => updateFormField("province", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Postal Code</label>
                        <input
                          value={formData.postal_code}
                          onChange={(e) => updateFormField("postal_code", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Country</label>
                        <input
                          value={formData.country}
                          onChange={(e) => updateFormField("country", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div id="paystubs-employment" className={getCardClassName("paystubs-employment")}>
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Employment & Pay Setup
                      </h2>
                      <p className="text-sm text-slate-500">
                        Salary amount is per pay period.
                      </p>
                    </div>
                    {isContractor && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        Contractors are not issued T4s; payroll deductions and remittances typically do not apply.
                      </div>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Worker Type</label>
                          <InfoTooltip text={TOOLTIPS.worker_type} label="Worker Type" />
                        </div>
                        <select
                          value={formData.worker_type}
                          onChange={(e) => updateFormField("worker_type", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        >
                          <option value="employee">Employee</option>
                          <option value="contractor">Contractor</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Employment Start Date</label>
                        <input
                          type="date"
                          value={formData.employment_start_date}
                          onChange={(e) => updateFormField("employment_start_date", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Pay Frequency</label>
                          <InfoTooltip text={TOOLTIPS.pay_frequency} label="Pay Frequency" />
                        </div>
                        <select
                          value={formData.pay_frequency}
                          onChange={(e) => updateFormField("pay_frequency", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Biweekly</option>
                          <option value="semi_monthly">Semi-monthly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Pay Type</label>
                          <InfoTooltip text={TOOLTIPS.pay_type} label="Pay Type" />
                        </div>
                        <select
                          value={formData.pay_type}
                          onChange={(e) => updateFormField("pay_type", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="hourly">Hourly</option>
                          <option value="salary">Salary</option>
                          <option value="commission">Commission</option>
                          <option value="hybrid">Hybrid</option>
                        </select>
                      </div>
                      {(payType === "hourly" || payType === "hybrid") && (
                        <div>
                          <label className="text-sm text-slate-600">Hourly Rate</label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.hourly_rate}
                            onChange={(e) => updateFormField("hourly_rate", e.target.value)}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>
                      )}
                      {payType === "salary" && (
                        <div>
                          <label className="text-sm text-slate-600">Salary Amount</label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.salary_amount}
                            onChange={(e) => updateFormField("salary_amount", e.target.value)}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Overtime Multiplier</label>
                          <InfoTooltip text={TOOLTIPS.overtime_multiplier} label="Overtime Multiplier" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.overtime_multiplier}
                          onChange={(e) => updateFormField("overtime_multiplier", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div id="paystubs-tax" className={getCardClassName("paystubs-tax")}>
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Tax Withholding (TD1)
                      </h2>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Federal claim amount</label>
                          <InfoTooltip text={TOOLTIPS.federal_td1} label="Federal claim amount" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.td1_federal_claim_amount}
                          onChange={(e) => updateFormField("td1_federal_claim_amount", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Provincial claim amount</label>
                          <InfoTooltip text={TOOLTIPS.provincial_td1} label="Provincial claim amount" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.td1_provincial_claim_amount}
                          onChange={(e) => updateFormField("td1_provincial_claim_amount", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">
                          Additional tax withholding
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.additional_tax_withholding}
                          onChange={(e) =>
                            updateFormField("additional_tax_withholding", e.target.value)
                          }
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Vacation Pay
                      </h2>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="text-sm text-slate-600">Vacation pay %</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.vacation_pay_percent}
                          onChange={(e) => updateFormField("vacation_pay_percent", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-600">Vacation pay method</label>
                          <InfoTooltip text={TOOLTIPS.vacation_method} label="Vacation pay method" />
                        </div>
                        <select
                          value={formData.vacation_pay_method}
                          onChange={(e) => updateFormField("vacation_pay_method", e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="accrue">Accrue</option>
                          <option value="payout_each_pay">Payout each pay</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div id="paystubs-deposit" className={getCardClassName("paystubs-deposit")}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                            Direct Deposit
                          </h2>
                          <InfoTooltip text={TOOLTIPS.direct_deposit} label="Direct Deposit" />
                        </div>
                        <p className="text-sm text-slate-500">
                          {detailMeta.direct_deposit_on_file ? "On file" : "Not on file"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowDirectDeposit((prev) => !prev)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        {showDirectDeposit ? "Cancel" : "Add/Update Direct Deposit"}
                      </button>
                    </div>
                    {showDirectDeposit && (
                      <div className="space-y-3">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div>
                            <label className="text-sm text-slate-600">Institution #</label>
                            <input
                              type="password"
                              autoComplete="off"
                              inputMode="numeric"
                              value={bankFields.institution}
                              onChange={(e) => updateBankField("institution", e.target.value)}
                              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-slate-600">Transit #</label>
                            <input
                              type="password"
                              autoComplete="off"
                              inputMode="numeric"
                              value={bankFields.transit}
                              onChange={(e) => updateBankField("transit", e.target.value)}
                              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-slate-600">Account #</label>
                            <input
                              type="password"
                              autoComplete="off"
                              inputMode="numeric"
                              value={bankFields.account}
                              onChange={(e) => updateBankField("account", e.target.value)}
                              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        {fieldErrors.direct_deposit && (
                          <div className="text-xs text-rose-600">
                            {fieldErrors.direct_deposit}
                          </div>
                        )}
                        <p className="text-xs text-slate-500">
                          Bank info is stored encrypted and will not be displayed after saving.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || loadingDetail}
                      className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
